import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'src/lib/utils';
import { Badge } from 'src/components/ui/badge';
import { RequestResponse } from 'src/lib/services/api/request-api';
import { useTranslation } from 'react-i18next';
import { requestStatusTranslationsMap } from 'src/features/requests/request.helper';

// Kept identical to Space's request-status-badge.tsx (#2756): explicit radius
// and colours, not the app-wide --radius / --brand, which differ per app.
const requestBadgeVariants = cva(
  'tw-rounded-[0.5rem] tw-border-transparent tw-font-medium tw-whitespace-nowrap',
  {
    variants: {
      variant: {
        draft: 'tw-bg-blue-300 tw-text-blue-500',
        uploading: 'tw-bg-amber-200 tw-text-amber-900',
        queued: 'tw-bg-neutral-200 tw-text-neutral-700',
        currently_editing: 'tw-bg-yellow-300 tw-text-yellow-900',
        needs_attention: 'tw-bg-amber-200 tw-text-amber-900',
        needs_reedit: 'tw-bg-red-200 tw-text-red-900',
        delivered: 'tw-bg-purple-200 tw-text-purple-500',
        complete: 'tw-bg-green-200 tw-text-green-900',
        archived: 'tw-bg-orange-200 tw-text-orange-900',
        unknown: 'tw-bg-neutral-200 tw-text-neutral-700',
      } as { [key: RequestResponse['status'] | string]: string },
      size: {
        sm: 'tw-px-2 tw-py-1 tw-text-sm',
        default: 'tw-px-4 tw-py-1 tw-text-base',
      },
    },
    defaultVariants: {
      variant: 'unknown',
      size: 'default',
    },
  },
);

type RequestStatusBadgeVariantProps = VariantProps<typeof requestBadgeVariants>;

export type RequestStatusBadgeProps = React.HTMLAttributes<HTMLDivElement> &
  Omit<RequestStatusBadgeVariantProps, 'variant'> & {
    variant?: string | RequestStatusBadgeVariantProps['variant'];
    iconPrefix?: React.ReactNode;
  };

export const variants: RequestStatusBadgeProps['variant'][] = [
  'draft',
  'uploading',
  'queued',
  'currently_editing',
  'needs_attention',
  'needs_reedit',
  'delivered',
  'complete',
  'archived',
];

function RequestStatusBadge({
  className,
  size,
  variant,
  iconPrefix,
  children,
  ...props
}: RequestStatusBadgeProps) {
  const { t } = useTranslation();

  const label = children ?? t(requestStatusTranslationsMap(`${variant}`));

  return (
    <Badge
      variant={'unset'}
      className={cn(
        requestBadgeVariants({ size, variant: variants?.includes(variant) ? variant : 'unknown' }),
        className,
      )}
      {...props}
    >
      {iconPrefix ? (
        <span className={'tw-inline-flex tw-items-center tw-gap-1'}>
          {iconPrefix}
          {label}
        </span>
      ) : (
        label
      )}
    </Badge>
  );
}
export { RequestStatusBadge };
